import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../Navbar";
import { getOrdenPagoById } from "./ApiServices";
import OrdenPagoWizard from "./OrdenPagoWizard";
import {
  buildEstadoWizardDesdeDetalle,
  esOpEditable,
  etiquetaEstadoOp,
  mapDetalleOrdenPagoResponse,
  obtenerLoteDeOrdenPago,
} from "./utils";

/**
 * Edición + aprobación de OP suelta en pendiente_aprobacion.
 * Las OP de un lote se gestionan desde el detalle del lote.
 */
export default function EditarOrdenPagoPendiente() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [estadoWizard, setEstadoWizard] = useState(null);
  const [idLoteRedirect, setIdLoteRedirect] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    setEstadoWizard(null);
    setIdLoteRedirect(null);

    try {
      const data = await getOrdenPagoById(id);
      const detalle = mapDetalleOrdenPagoResponse(data);
      const estado = detalle?.ordenPago?.estado;
      const lote =
        obtenerLoteDeOrdenPago(data?.orden_pago) ??
        obtenerLoteDeOrdenPago(data) ??
        obtenerLoteDeOrdenPago(detalle?.ordenPago);

      if (lote?.id) {
        setIdLoteRedirect(lote.id);
        setError(
          `Esta orden forma parte del lote ${
            lote.nro_lote ?? lote.id
          }. Aprobala o anúlala desde el lote.`
        );
        return;
      }

      if (!esOpEditable(estado)) {
        setError(
          `Solo se pueden editar órdenes en pendiente de aprobación. Estado actual: ${etiquetaEstadoOp(
            estado,
            detalle?.ordenPago?.estado_label
          )}`
        );
        return;
      }

      setEstadoWizard(buildEstadoWizardDesdeDetalle(detalle, data));
    } catch (err) {
      setError(err.message || "No se pudo cargar la orden de pago");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (loading) {
    return (
      <>
        <Navbar />
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error || !estadoWizard) {
    return (
      <>
        <Navbar />
        <Container maxWidth="md" sx={{ pt: 4, pb: 4 }}>
          <Stack spacing={2}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={cargar}>
                  Reintentar
                </Button>
              }
            >
              {error || "No se pudo iniciar la aprobación"}
            </Alert>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/ordenes_pago_proveedores")}
              >
                Volver al listado
              </Button>
              {idLoteRedirect ? (
                <Button
                  variant="contained"
                  onClick={() => navigate(`/lotes_orden_pago/${idLoteRedirect}`)}
                >
                  Ir al lote
                </Button>
              ) : (
                <Button
                  variant="text"
                  onClick={() => navigate(`/ordenes_pago_proveedores/${id}`)}
                >
                  Ver detalle
                </Button>
              )}
            </Stack>
          </Stack>
        </Container>
      </>
    );
  }

  return (
    <OrdenPagoWizard
      initialStep={1}
      estadoInicial={estadoWizard}
    />
  );
}
